import { createClient, type SupabaseClient } from "@supabase/supabase-js";
import { cookieStorage } from "./cookieStorage";

declare global {
  interface ImportMetaEnv {
    readonly VITE_SUPABASE_URL: string;
    readonly VITE_SUPABASE_ANON_KEY: string;
    readonly VITE_SUPABASE_STORAGE_KEY?: string;
  }
  interface ImportMeta {
    readonly env: ImportMetaEnv;
  }
}

let supabase: SupabaseClient | null = null;

/**
 * Get the singleton Supabase client for Vite apps.
 * Reads VITE_SUPABASE_URL, VITE_SUPABASE_ANON_KEY, and optionally
 * VITE_SUPABASE_STORAGE_KEY from import.meta.env. Set
 * VITE_SUPABASE_STORAGE_KEY per-app to isolate auth tokens in cookies
 * across apps.
 *
 * @param fallbackStorageKey - Used when VITE_SUPABASE_STORAGE_KEY is not set.
 *   Pass an app-specific key (e.g. "venuehub_admin_token") to ensure cookie
 *   isolation even without the env var configured.
 */
export function getSupabase(fallbackStorageKey?: string): SupabaseClient {
  if (supabase) return supabase;

  const url = import.meta.env.VITE_SUPABASE_URL;
  const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
  const storageKey = import.meta.env.VITE_SUPABASE_STORAGE_KEY || fallbackStorageKey;
  const isDev = (import.meta.env as { DEV?: boolean }).DEV === true;

  if (!url || !anonKey) {
    throw new Error(
      "Missing VITE_SUPABASE_URL or VITE_SUPABASE_ANON_KEY env vars"
    );
  }

  supabase = createClient(url, anonKey, {
    auth: {
      storage: cookieStorage,
      autoRefreshToken: true,
      persistSession: true,
      detectSessionInUrl: true,
      ...(storageKey && { storageKey }),
      // In dev, React Strict Mode double-mounts components which orphans the
      // navigator lock Supabase uses for token coordination. Bypass the lock
      // in dev to silence the 5 s timeout warning — safe for single-tab dev.
      ...(isDev && {
        lock: async <R>(_name: string, _acquireTimeout: number, fn: () => Promise<R>): Promise<R> => fn(),
      }),
    },
  });
  return supabase;
}

export {
  createClient,
  type SupabaseClient,
  type Session,
  type User,
} from "@supabase/supabase-js";
